package com.sweetlime.dungeonamigo.presentation.features.compendium

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.cache.Items
import com.sweetlime.dungeonamigo.cache.Monsters
import com.sweetlime.dungeonamigo.cache.Spells
import com.sweetlime.dungeonamigo.compendium.CompendiumUiState
import com.sweetlime.dungeonamigo.compendium.CompendiumViewModel
import com.sweetlime.dungeonamigo.presentation.navigation.NavigationController
import com.sweetlime.dungeonamigo.presentation.ui.components.CompendiumSyncIllustration
import com.sweetlime.dungeonamigo.presentation.ui.components.EmptyStateView
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import org.koin.compose.koinInject

object CompendiumScreen : Screen {
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: CompendiumViewModel = koinInject()
        val navController: NavigationController = koinInject()
        val activeCharacterId by navController.activeCharacterId.collectAsState()
        val state by vm.state.collectAsState()

        when (val s = state) {
            is CompendiumUiState.Syncing -> SyncingView(s.category, s.percent)
            is CompendiumUiState.Ready   -> ReadyView(vm, navigator, activeCharacterId)
            is CompendiumUiState.Error   -> ErrorView(s.message) { vm.retry() }
        }
    }
}

@Composable
private fun SyncingView(category: String, percent: Int) {
    Column(
        modifier = Modifier.fillMaxSize().background(BrandColors.Vault),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        EmptyStateView(
            illustration = { CompendiumSyncIllustration() },
            headline = "Gathering the knowledge",
            subtext = if (category.isNotEmpty())
                "Downloading ${category.replaceFirstChar { it.uppercase() }}..."
            else
                "Downloading the SRD compendium for offline use...",
        )
        Spacer(Modifier.height(20.dp))
        LinearProgressIndicator(
            progress = { if (percent > 0) percent / 100f else 0f },
            modifier = Modifier.width(240.dp).height(8.dp).clip(RoundedCornerShape(4.dp)),
            color = BrandColors.Parchment,
            trackColor = BrandColors.Stone,
        )
        Spacer(Modifier.height(8.dp))
        if (percent > 0) {
            Text("$percent%", color = BrandColors.IvoryDim, fontSize = 12.sp)
        }
    }
}

@Composable
private fun ErrorView(message: String, onRetry: () -> Unit) {
    Column(
        modifier = Modifier.fillMaxSize().background(BrandColors.Vault).padding(24.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text("Sync Failed", color = BrandColors.Ember, fontWeight = FontWeight.Bold, fontSize = 18.sp)
        Spacer(Modifier.height(8.dp))
        Text(message, color = BrandColors.IvoryDim, fontSize = 13.sp)
        Spacer(Modifier.height(24.dp))
        Button(onClick = onRetry, colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember)) {
            Text("Retry", color = BrandColors.Ivory)
        }
    }
}

@Composable
private fun ReadyView(
    vm: CompendiumViewModel,
    navigator: cafe.adriel.voyager.navigator.Navigator,
    activeCharacterId: String?,
) {
    var selectedTab by remember { mutableIntStateOf(0) }
    val spellResults   by vm.spells.collectAsState()
    val monsterResults by vm.monsters.collectAsState()
    val itemResults    by vm.items.collectAsState()
    var spellQuery   by remember { mutableStateOf("") }
    var monsterQuery by remember { mutableStateOf("") }
    var itemQuery    by remember { mutableStateOf("") }

    Column(modifier = Modifier.fillMaxSize().background(BrandColors.Vault)) {
        // Sticky controls
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(BrandColors.Vault)
                .padding(top = 4.dp),
        ) {
            // Segmented tabs
            SegmentedTabs(
                tabs = listOf("Spells", "Monsters", "Items"),
                selected = selectedTab,
                onSelect = { selectedTab = it },
            )
            // Search field
            val (query, onQueryChange) = when (selectedTab) {
                0    -> spellQuery to { v: String -> spellQuery = v; vm.updateSpellQuery(v) }
                1    -> monsterQuery to { v: String -> monsterQuery = v; vm.updateMonsterQuery(v) }
                else -> itemQuery to { v: String -> itemQuery = v; vm.updateItemQuery(v) }
            }
            SearchBar(
                query = query,
                placeholder = "Search ${listOf("spells", "monsters", "items")[selectedTab]}…",
                onQueryChange = onQueryChange,
            )
        }

        when (selectedTab) {
            0 -> LazyColumn(Modifier.fillMaxSize()) {
                items(spellResults, key = { it.slug }) { spell ->
                    SpellRow(spell, onClick = { navigator.push(SpellDetailScreen(spell.slug, activeCharacterId)) })
                }
            }
            1 -> LazyColumn(Modifier.fillMaxSize()) {
                items(monsterResults, key = { it.slug }) { monster ->
                    MonsterRow(monster, onClick = { navigator.push(MonsterDetailScreen(monster.slug)) })
                }
            }
            2 -> LazyColumn(Modifier.fillMaxSize()) {
                items(itemResults, key = { it.slug }) { item -> ItemRow(item) }
            }
        }
    }
}

@Composable
private fun SegmentedTabs(tabs: List<String>, selected: Int, onSelect: (Int) -> Unit) {
    val pf = pixelFont()
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 18.dp, vertical = 6.dp)
            .background(BrandColors.Stone, RoundedCornerShape(12.dp))
            .padding(5.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        tabs.forEachIndexed { index, tab ->
            val isSelected = index == selected
            Box(
                modifier = Modifier
                    .weight(1f)
                    .clip(RoundedCornerShape(8.dp))
                    .background(
                        if (isSelected) BrandColors.Ember else Color.Transparent
                    )
                    .clickable { onSelect(index) }
                    .padding(vertical = 8.dp),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = tab,
                    color = if (isSelected) BrandColors.Ivory else BrandColors.IvoryDim,
                    fontFamily = if (isSelected) pf else null,
                    fontSize = 14.5.sp,
                    fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
                )
            }
        }
    }
}

@Composable
private fun SearchBar(query: String, placeholder: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        placeholder = { Text(placeholder, color = BrandColors.IvoryDim, fontSize = 13.sp) },
        leadingIcon = { Icon(Icons.Default.Search, null, tint = BrandColors.SlateLight) },
        singleLine = true,
        colors = OutlinedTextFieldDefaults.colors(
            focusedContainerColor   = BrandColors.Stone,
            unfocusedContainerColor = BrandColors.Stone,
            focusedBorderColor      = BrandColors.Parchment,
            unfocusedBorderColor    = BrandColors.Slate.copy(alpha = 0.4f),
            focusedTextColor        = BrandColors.Ivory,
            unfocusedTextColor      = BrandColors.Ivory,
            cursorColor             = BrandColors.Ember,
        ),
        shape = RoundedCornerShape(11.dp),
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 18.dp, vertical = 6.dp)
            .height(46.dp),
    )
}

// ─── Spell rows ─────────────────────────────────────────────────────────────

@Composable
private fun SpellRow(spell: Spells, onClick: () -> Unit) {
    val schoolColor = schoolColorFor(spell.school)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 18.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        SchoolPip(school = spell.school, size = 38)
        Column(Modifier.weight(1f)) {
            Text(spell.name, color = BrandColors.Ivory, fontWeight = FontWeight.Medium, fontSize = 15.sp)
            val lvlInt = spell.level?.toIntOrNull()
            val subtitle = listOfNotNull(
                lvlInt?.let { if (it == 0) "Cantrip" else "Level $it" },
                spell.school?.replaceFirstChar { it.uppercase() },
            ).joinToString(" · ")
            if (subtitle.isNotEmpty()) Text(subtitle, color = BrandColors.IvoryDim, fontSize = 12.sp)
        }
        // Level badge
        val lvl = spell.level
        val lvlInt = lvl?.toIntOrNull()
        if (lvl != null) {
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .background(schoolColor.copy(alpha = 0.11f))
                    .border(1.dp, schoolColor.copy(alpha = 0.33f), RoundedCornerShape(6.dp))
                    .padding(horizontal = 7.dp, vertical = 3.dp),
            ) {
                Text(
                    if (lvlInt == 0) "C" else "$lvl",
                    color = schoolColor,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
    }
    HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f), modifier = Modifier.padding(horizontal = 18.dp))
}

// ─── Monster rows ─────────────────────────────────────────────────────────────

@Composable
private fun MonsterRow(monster: Monsters, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 18.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        // Monster type pip (use Ember as the monster "school")
        SchoolPip(school = monster.type, size = 38, defaultColor = BrandColors.Ember)
        Column(Modifier.weight(1f)) {
            Text(monster.name, color = BrandColors.Ivory, fontWeight = FontWeight.Medium, fontSize = 15.sp)
            val subtitle = listOfNotNull(
                monster.challenge_rating?.let { "CR $it" },
                monster.type?.replaceFirstChar { it.uppercase() },
            ).joinToString(" · ")
            if (subtitle.isNotEmpty()) Text(subtitle, color = BrandColors.IvoryDim, fontSize = 12.sp)
        }
        monster.challenge_rating?.let { cr ->
            val crDouble = cr.toDoubleOrNull() ?: 0.0
            val crColor = if (crDouble >= 10.0) BrandColors.Ember else BrandColors.Parchment
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .background(crColor.copy(alpha = 0.11f))
                    .border(1.dp, crColor.copy(alpha = 0.33f), RoundedCornerShape(6.dp))
                    .padding(horizontal = 7.dp, vertical = 3.dp),
            ) {
                Text("CR $cr", color = crColor, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
            }
        }
    }
    HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f), modifier = Modifier.padding(horizontal = 18.dp))
}

// ─── Item rows ─────────────────────────────────────────────────────────────────

@Composable
private fun ItemRow(item: Items) {
    val rarityColor = rarityColorFor(item.rarity)
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 18.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Column(Modifier.weight(1f)) {
            Text(item.name, color = BrandColors.Ivory, fontWeight = FontWeight.Medium, fontSize = 15.sp)
            val subtitle = listOfNotNull(item.type, item.rarity).joinToString(" · ")
            if (subtitle.isNotEmpty()) Text(subtitle, color = BrandColors.IvoryDim, fontSize = 12.sp)
        }
        if (item.rarity != null) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(5.dp),
            ) {
                Box(Modifier.size(7.dp).background(rarityColor, RoundedCornerShape(50.dp)))
                Text(item.rarity, color = rarityColor, fontSize = 11.sp)
            }
        }
    }
    HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f), modifier = Modifier.padding(horizontal = 18.dp))
}

// ─── SchoolPip ────────────────────────────────────────────────────────────────

@Composable
private fun SchoolPip(school: String?, size: Int, defaultColor: Color = BrandColors.SlateLight) {
    val color = if (school != null) schoolColorFor(school) else defaultColor
    Box(
        modifier = Modifier
            .size(size.dp)
            .clip(RoundedCornerShape(8.dp))
            .background(
                Brush.verticalGradient(listOf(color.copy(alpha = 0.15f), BrandColors.Vault))
            )
            .border(1.dp, color.copy(alpha = 0.33f), RoundedCornerShape(8.dp)),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = schoolInitial(school),
            color = color,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
        )
    }
}

private fun schoolColorFor(school: String?): Color = when (school?.lowercase()) {
    "illusion", "enchantment"    -> Color(0xFF9B6FD9)
    "transmutation", "divination" -> BrandColors.Parchment
    "evocation"                  -> BrandColors.Ember
    "conjuration", "abjuration"  -> BrandColors.SpellBlue
    "necromancy"                 -> BrandColors.SlateLight
    else                         -> BrandColors.SlateLight
}

private fun schoolInitial(school: String?): String = when (school?.lowercase()) {
    "illusion"      -> "I"
    "transmutation" -> "T"
    "evocation"     -> "Ev"
    "conjuration"   -> "C"
    "abjuration"    -> "A"
    "divination"    -> "D"
    "necromancy"    -> "N"
    "enchantment"   -> "En"
    else            -> "?"
}

private fun rarityColorFor(rarity: String?): Color = when (rarity?.lowercase()) {
    "common"    -> BrandColors.IvoryDim
    "uncommon"  -> BrandColors.Venom
    "rare"      -> BrandColors.SpellBlue
    "very rare" -> Color(0xFF9B6FD9)
    "legendary" -> BrandColors.Parchment
    else        -> BrandColors.IvoryDim
}
